import React, { useState, useEffect } from 'react';
import api from '../api';
import ConfirmDeleteModal from './ConfirmDeleteModal';

interface CarrierService {
  id: number;
  code: string;
  name: string;
  service_type: string | null;
  carrier_product_code: string | null;
  carrier_sub_product_code: string | null;
  carrier_product_type: number | null;
  supports_cod: boolean;
  supports_prepaid: boolean;
  supports_reverse: boolean;
  is_active: boolean;
  estimated_days_min: number | null;
  estimated_days_max: number | null;
}

interface CarrierCapability {
  id: number;
  capability: string;
  is_supported: boolean;
  config: any;
}

interface CarrierCredential {
  id: number;
  environment: string;
  label: string;
  is_active: boolean;
  validation_status: string | null;
  last_validated_at: string | null;
}

interface Carrier {
  id: number;
  code: string;
  name: string;
  display_name: string;
  status: string;
  environment: string;
  auth_type: string;
  priority: number;
  is_healthy: boolean;
  last_health_check_at: string | null;
  avg_response_time_ms: number | null;
  supported_countries: string[] | null;
  max_weight_kg: number | null;
  has_credentials: boolean;
  services_count: number;
  capabilities_count: number;
  adapter_available: boolean;
  created_at: string | null;
  // Detail fields
  base_url_sandbox?: string;
  base_url_production?: string;
  tracking_url_template?: string;
  cost_weight?: number;
  config?: any;
  services?: CarrierService[];
  capabilities?: CarrierCapability[];
  credentials?: CarrierCredential[];
}

interface ChannelAssignment {
  id: number;
  channel_id: number;
  carrier_service_id: number | null;
  is_default: boolean;
  priority: number;
  is_active: boolean;
  payment_mode: string | null;
  max_weight_kg: number | null;
  config: any;
}

interface CarrierManagerProps {
  userRole?: string;
}

const STATUS_COLORS: Record<string, {bg: string; color: string}> = {
  active: { bg: '#d4edda', color: '#155724' },
  inactive: { bg: '#f8d7da', color: '#721c24' },
  sandbox: { bg: '#fff3cd', color: '#856404' },
  suspended: { bg: '#d6d8db', color: '#383d41' },
};

const CAPABILITIES_LIST = [
  'cod', 'prepaid', 'reverse_pickup', 'surface', 'air', 'international',
  'insurance', 'multi_piece', 'pickup_scheduling', 'manifest_generation',
  'pod_download', 'appointment_delivery',
];

const CarrierManager: React.FC<CarrierManagerProps> = ({ userRole = 'viewer' }) => {
  const [carriers, setCarriers] = useState<Carrier[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCarrier, setSelectedCarrier] = useState<Carrier | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showCredForm, setShowCredForm] = useState(false);
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [healthCheckResult, setHealthCheckResult] = useState<any>(null);
  const [testingConnection, setTestingConnection] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{show: boolean; id: number | null; name: string}>({show: false, id: null, name: ''});

  const canEdit = userRole === 'admin' || userRole === 'manager';
  const isAdmin = userRole === 'admin';

  // Create form state
  const [createForm, setCreateForm] = useState({
    code: '', name: '', display_name: '', auth_type: 'api_key',
    base_url_sandbox: '', base_url_production: '', priority: 0,
  });

  // Credential form state
  const [credForm, setCredForm] = useState<Record<string, string>>({});
  const [credEnv, setCredEnv] = useState('production');
  const [credLabel, setCredLabel] = useState('');

  // Service form state
  const [serviceForm, setServiceForm] = useState({
    code: '', name: '', service_type: 'express',
    carrier_product_code: '', carrier_sub_product_code: '',
    carrier_product_type: 1,
    supports_cod: false, supports_prepaid: true, supports_reverse: false,
    estimated_days_min: '', estimated_days_max: '',
  });

  useEffect(() => { fetchCarriers(); }, []);

  const fetchCarriers = async () => {
    setLoading(true);
    try {
      const res = await api.get('/shipping/carriers/');
      setCarriers(Array.isArray(res.data) ? res.data : []);
    } catch (e) { console.error('Failed to fetch carriers:', e); }
    finally { setLoading(false); }
  };

  const fetchCarrierDetail = async (id: number) => {
    setDetailLoading(true);
    try {
      const res = await api.get(`/shipping/carriers/${id}`);
      setSelectedCarrier(res.data);
    } catch (e) { console.error('Failed to fetch carrier detail:', e); }
    finally { setDetailLoading(false); }
  };

  const handleCreate = async () => {
    try {
      await api.post('/shipping/carriers/', createForm);
      setShowCreateForm(false);
      setCreateForm({ code: '', name: '', display_name: '', auth_type: 'api_key', base_url_sandbox: '', base_url_production: '', priority: 0 });
      fetchCarriers();
    } catch (e: any) { alert(e.response?.data?.detail || 'Failed to create carrier'); }
  };

  const handleStatusToggle = async (id: number, newStatus: string) => {
    try {
      await api.put(`/shipping/carriers/${id}`, { status: newStatus });
      fetchCarriers();
      if (selectedCarrier?.id === id) fetchCarrierDetail(id);
    } catch (e: any) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const handleEnvironmentToggle = async (id: number, env: string) => {
    try {
      await api.put(`/shipping/carriers/${id}`, { environment: env });
      if (selectedCarrier?.id === id) fetchCarrierDetail(id);
    } catch (e: any) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const handleTestConnection = async (id: number) => {
    setTestingConnection(true);
    setHealthCheckResult(null);
    try {
      const res = await api.post(`/shipping/carriers/${id}/test-connection`);
      setHealthCheckResult(res.data);
    } catch (e: any) {
      setHealthCheckResult({ is_healthy: false, message: e.response?.data?.detail || 'Connection failed' });
    } finally { setTestingConnection(false); }
  };

  const handleSaveCredentials = async () => {
    if (!selectedCarrier) return;
    try {
      await api.post(`/shipping/carriers/${selectedCarrier.id}/credentials`, {
        environment: credEnv,
        label: credLabel || `${credEnv} credentials`,
        credentials: credForm,
      });
      setShowCredForm(false);
      setCredForm({});
      fetchCarrierDetail(selectedCarrier.id);
      alert('Credentials saved (encrypted)');
    } catch (e: any) { alert(e.response?.data?.detail || 'Failed to save credentials'); }
  };

  const handleAddService = async () => {
    if (!selectedCarrier) return;
    try {
      await api.post(`/shipping/carriers/${selectedCarrier.id}/services`, {
        ...serviceForm,
        carrier_product_type: Number(serviceForm.carrier_product_type) || null,
        estimated_days_min: serviceForm.estimated_days_min ? Number(serviceForm.estimated_days_min) : null,
        estimated_days_max: serviceForm.estimated_days_max ? Number(serviceForm.estimated_days_max) : null,
      });
      setShowServiceForm(false);
      setServiceForm({ code: '', name: '', service_type: 'express', carrier_product_code: '', carrier_sub_product_code: '', carrier_product_type: 1, supports_cod: false, supports_prepaid: true, supports_reverse: false, estimated_days_min: '', estimated_days_max: '' });
      fetchCarrierDetail(selectedCarrier.id);
    } catch (e: any) { alert(e.response?.data?.detail || 'Failed to add service'); }
  };

  const handleUpdateCapabilities = async (caps: Record<string, boolean>) => {
    if (!selectedCarrier) return;
    try {
      await api.put(`/shipping/carriers/${selectedCarrier.id}/capabilities`, { capabilities: caps });
      fetchCarrierDetail(selectedCarrier.id);
    } catch (e: any) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteCarrier = async () => {
    if (!deleteConfirm.id) return;
    try {
      await api.delete(`/shipping/carriers/${deleteConfirm.id}`);
      setDeleteConfirm({ show: false, id: null, name: '' });
      setSelectedCarrier(null);
      fetchCarriers();
    } catch (e: any) { alert(e.response?.data?.detail || 'Failed to delete'); }
  };

  const handleDeleteService = async (serviceId: number) => {
    if (!selectedCarrier) return;
    try {
      await api.delete(`/shipping/carriers/${selectedCarrier.id}/services/${serviceId}`);
      fetchCarrierDetail(selectedCarrier.id);
    } catch (e: any) { alert(e.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading carriers...</div>;

  return (
    <div className="split-master-detail" style={{ height: '100%', background: '#f4f6f9' }}>
      {/* Carrier List Panel */}
      <div className="split-list-plain" style={{ borderRight: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', background: 'white' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#1e293b' }}>Carriers</h3>
          {isAdmin && (
            <button onClick={() => setShowCreateForm(true)} style={{ padding: '4px 10px', fontSize: '12px', fontWeight: 600, borderRadius: '6px', border: 'none', background: '#0d9488', color: 'white', cursor: 'pointer' }}>
              + Add
            </button>
          )}
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: '8px' }}>
          {carriers.map(c => (
            <div
              key={c.id}
              onClick={() => fetchCarrierDetail(c.id)}
              style={{
                padding: '12px 14px', marginBottom: '4px', borderRadius: '8px', cursor: 'pointer',
                background: selectedCarrier?.id === c.id ? '#f0fdfa' : 'white',
                border: selectedCarrier?.id === c.id ? '1px solid #0d9488' : '1px solid transparent',
                transition: '150ms',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>{c.display_name || c.name}</span>
                <span style={{
                  padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: 600,
                  background: STATUS_COLORS[c.status]?.bg || '#f1f5f9',
                  color: STATUS_COLORS[c.status]?.color || '#475569',
                }}>{c.status}</span>
              </div>
              <div style={{ display: 'flex', gap: '8px', marginTop: '4px', fontSize: '11px', color: '#64748b' }}>
                <span style={{ fontFamily: 'monospace' }}>{c.code}</span>
                {c.is_healthy ? <span style={{ color: '#10b981' }}>healthy</span> : <span style={{ color: '#ef4444' }}>unhealthy</span>}
                {c.adapter_available && <span style={{ color: '#6366f1' }}>adapter ready</span>}
              </div>
            </div>
          ))}
          {carriers.length === 0 && (
            <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>
              No carriers configured. Click "+ Add" to register one.
            </div>
          )}
        </div>
      </div>

      {/* Detail Panel */}
      <div style={{ flex: 1, overflow: 'auto', padding: '24px' }}>
        {!selectedCarrier && !showCreateForm && (
          <div style={{ textAlign: 'center', color: '#94a3b8', paddingTop: '80px' }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
            <p style={{ marginTop: '12px', fontSize: '14px' }}>Select a carrier to view configuration</p>
          </div>
        )}

        {/* Create Carrier Form */}
        {showCreateForm && (
          <div style={{ background: 'white', borderRadius: '12px', padding: '24px', border: '1px solid #e2e8f0', maxWidth: '600px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 700, marginBottom: '16px' }}>Register New Carrier</h3>
            <div className="form-grid-2" style={{ gap: '12px', marginBottom: 0 }}>
              <div>
                <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>Code *</label>
                <input value={createForm.code} onChange={e => setCreateForm({...createForm, code: e.target.value})} placeholder="bluedart" style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>Name *</label>
                <input value={createForm.name} onChange={e => setCreateForm({...createForm, name: e.target.value})} placeholder="Blue Dart Express" style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>Auth Type</label>
                <select value={createForm.auth_type} onChange={e => setCreateForm({...createForm, auth_type: e.target.value})} style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }}>
                  <option value="api_key">API Key</option>
                  <option value="oauth2">OAuth 2.0</option>
                  <option value="jwt">JWT</option>
                  <option value="basic">Basic Auth</option>
                  <option value="hmac">HMAC</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>Priority</label>
                <input type="number" value={createForm.priority} onChange={e => setCreateForm({...createForm, priority: Number(e.target.value)})} style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>Sandbox URL</label>
                <input value={createForm.base_url_sandbox} onChange={e => setCreateForm({...createForm, base_url_sandbox: e.target.value})} placeholder="https://staging-api.carrier.com" style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>Production URL</label>
                <input value={createForm.base_url_production} onChange={e => setCreateForm({...createForm, base_url_production: e.target.value})} placeholder="https://api.carrier.com" style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
              <button onClick={handleCreate} style={{ padding: '6px 14px', fontSize: '13px', fontWeight: 600, borderRadius: '6px', border: 'none', background: '#0d9488', color: 'white', cursor: 'pointer' }}>Create</button>
              <button className="btn btn-cancel" onClick={() => setShowCreateForm(false)}>Cancel</button>
            </div>
          </div>
        )}

        {/* Carrier Detail */}
        {selectedCarrier && !showCreateForm && (
          <div>
            {/* Header */}
            <div style={{ background: 'white', borderRadius: '12px', padding: '20px 24px', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#1e293b' }}>{selectedCarrier.display_name || selectedCarrier.name}</h2>
                  <span style={{ fontFamily: "'SF Mono', monospace", fontSize: '12px', color: '#64748b' }}>{selectedCarrier.code}</span>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  {/* Status toggle */}
                  {isAdmin && (
                    <select
                      value={selectedCarrier.status}
                      onChange={e => handleStatusToggle(selectedCarrier.id, e.target.value)}
                      style={{ padding: '4px 10px', fontSize: '12px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="sandbox">Sandbox</option>
                      <option value="suspended">Suspended</option>
                    </select>
                  )}
                  {/* Environment toggle */}
                  <select
                    value={selectedCarrier.environment}
                    onChange={e => handleEnvironmentToggle(selectedCarrier.id, e.target.value)}
                    style={{ padding: '4px 10px', fontSize: '12px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                  >
                    <option value="sandbox">Sandbox</option>
                    <option value="production">Production</option>
                  </select>
                  {/* Test connection */}
                  <button
                    onClick={() => handleTestConnection(selectedCarrier.id)}
                    disabled={testingConnection}
                    style={{ padding: '4px 12px', fontSize: '12px', fontWeight: 600, borderRadius: '6px', border: '1px solid #e2e8f0', background: 'white', cursor: 'pointer' }}
                  >
                    {testingConnection ? 'Testing...' : 'Test Connection'}
                  </button>
                  {/* Delete */}
                  {isAdmin && selectedCarrier.status !== 'active' && (
                    <button onClick={() => setDeleteConfirm({ show: true, id: selectedCarrier.id, name: selectedCarrier.name })}
                      style={{ padding: '4px 12px', fontSize: '12px', fontWeight: 600, borderRadius: '6px', border: '1px solid #fca5a5', background: '#fee2e2', color: '#991b1b', cursor: 'pointer' }}>
                      Delete
                    </button>
                  )}
                </div>
              </div>
              {/* Health check result */}
              {healthCheckResult && (
                <div style={{ marginTop: '12px', padding: '8px 12px', borderRadius: '6px', fontSize: '12px', background: healthCheckResult.is_healthy ? '#d4edda' : '#f8d7da', color: healthCheckResult.is_healthy ? '#155724' : '#721c24' }}>
                  {healthCheckResult.is_healthy ? '✓' : '✗'} {healthCheckResult.message} {healthCheckResult.response_time_ms ? `(${healthCheckResult.response_time_ms}ms)` : ''}
                </div>
              )}
            </div>

            {/* Credentials Section */}
            <div style={{ background: 'white', borderRadius: '12px', padding: '20px 24px', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: '#1e293b' }}>Credentials</h4>
                {isAdmin && <button onClick={() => setShowCredForm(true)} style={{ padding: '3px 10px', fontSize: '11px', fontWeight: 600, borderRadius: '6px', border: 'none', background: '#0d9488', color: 'white', cursor: 'pointer' }}>+ Add Credentials</button>}
              </div>
              {selectedCarrier.credentials && selectedCarrier.credentials.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {selectedCarrier.credentials.map(cred => (
                    <div key={cred.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: '#f8f9fa', borderRadius: '6px', fontSize: '12px' }}>
                      <div>
                        <span style={{ fontWeight: 600 }}>{cred.label}</span>
                        <span style={{ marginLeft: '8px', color: '#64748b' }}>({cred.environment})</span>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <span style={{ color: cred.is_active ? '#10b981' : '#94a3b8' }}>{cred.is_active ? 'Active' : 'Inactive'}</span>
                        {cred.validation_status && <span style={{ padding: '2px 6px', borderRadius: '4px', fontSize: '10px', background: cred.validation_status === 'valid' ? '#d4edda' : '#f8d7da' }}>{cred.validation_status}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ fontSize: '12px', color: '#94a3b8', margin: 0 }}>No credentials configured. Add credentials to enable this carrier.</p>
              )}

              {/* Credential Form */}
              {showCredForm && (
                <div style={{ marginTop: '12px', padding: '16px', background: '#f8f9fa', borderRadius: '8px' }}>
                  <div className="form-grid-2" style={{ gap: '10px', marginBottom: '12px' }}>
                    <div>
                      <label style={{ fontSize: '11px', fontWeight: 600, display: 'block', marginBottom: '2px' }}>Environment</label>
                      <select value={credEnv} onChange={e => setCredEnv(e.target.value)} style={{ width: '100%', padding: '6px 10px', fontSize: '12px', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
                        <option value="sandbox">Sandbox</option>
                        <option value="production">Production</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize: '11px', fontWeight: 600, display: 'block', marginBottom: '2px' }}>Label</label>
                      <input value={credLabel} onChange={e => setCredLabel(e.target.value)} placeholder="Main account" style={{ width: '100%', padding: '6px 10px', fontSize: '12px', border: '1px solid #e2e8f0', borderRadius: '6px', boxSizing: 'border-box' }} />
                    </div>
                  </div>
                  <p style={{ fontSize: '11px', color: '#64748b', margin: '0 0 8px' }}>Enter credentials as key-value pairs (stored encrypted):</p>
                  {['client_id', 'client_secret', 'login_id', 'licence_key', 'customer_code', 'origin_area', 'api_type'].map(field => (
                    <div key={field} style={{ display: 'flex', gap: '8px', marginBottom: '6px', alignItems: 'center' }}>
                      <label style={{ width: '120px', fontSize: '11px', fontWeight: 500, color: '#475569' }}>{field}</label>
                      <input
                        type={field.includes('secret') || field.includes('key') ? 'password' : 'text'}
                        value={credForm[field] || ''}
                        onChange={e => setCredForm({...credForm, [field]: e.target.value})}
                        style={{ flex: 1, padding: '5px 10px', fontSize: '12px', border: '1px solid #e2e8f0', borderRadius: '6px' }}
                      />
                    </div>
                  ))}
                  <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                    <button onClick={handleSaveCredentials} style={{ padding: '5px 12px', fontSize: '12px', fontWeight: 600, borderRadius: '6px', border: 'none', background: '#0d9488', color: 'white', cursor: 'pointer' }}>Save Credentials</button>
                    <button className="btn btn-cancel btn-sm" onClick={() => { setShowCredForm(false); setCredForm({}); }}>Cancel</button>
                  </div>
                </div>
              )}
            </div>

            {/* Services Section */}
            <div style={{ background: 'white', borderRadius: '12px', padding: '20px 24px', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: '#1e293b' }}>Services</h4>
                {isAdmin && <button onClick={() => setShowServiceForm(true)} style={{ padding: '3px 10px', fontSize: '11px', fontWeight: 600, borderRadius: '6px', border: 'none', background: '#0d9488', color: 'white', cursor: 'pointer' }}>+ Add Service</button>}
              </div>
              {selectedCarrier.services && selectedCarrier.services.length > 0 ? (
                <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f8fafb', textTransform: 'uppercase', fontSize: '11px', fontWeight: 600, color: '#64748b', letterSpacing: '0.5px' }}>
                      <th style={{ padding: '8px 10px', textAlign: 'left' }}>Code</th>
                      <th style={{ padding: '8px 10px', textAlign: 'left' }}>Name</th>
                      <th style={{ padding: '8px 10px', textAlign: 'left' }}>Type</th>
                      <th style={{ padding: '8px 10px', textAlign: 'center' }}>Product Code</th>
                      <th style={{ padding: '8px 10px', textAlign: 'center' }}>COD</th>
                      <th style={{ padding: '8px 10px', textAlign: 'center' }}>Reverse</th>
                      <th style={{ padding: '8px 10px', textAlign: 'center' }}>ETA</th>
                      <th style={{ padding: '8px 10px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedCarrier.services.map(svc => (
                      <tr key={svc.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '8px 10px', fontFamily: 'monospace' }}>{svc.code}</td>
                        <td style={{ padding: '8px 10px' }}>{svc.name}</td>
                        <td style={{ padding: '8px 10px' }}>{svc.service_type}</td>
                        <td style={{ padding: '8px 10px', textAlign: 'center', fontFamily: 'monospace' }}>{svc.carrier_product_code}/{svc.carrier_sub_product_code}/{svc.carrier_product_type}</td>
                        <td style={{ padding: '8px 10px', textAlign: 'center' }}>{svc.supports_cod ? '✓' : '—'}</td>
                        <td style={{ padding: '8px 10px', textAlign: 'center' }}>{svc.supports_reverse ? '✓' : '—'}</td>
                        <td style={{ padding: '8px 10px', textAlign: 'center' }}>{svc.estimated_days_min && svc.estimated_days_max ? `${svc.estimated_days_min}-${svc.estimated_days_max}d` : '—'}</td>
                        <td style={{ padding: '8px 10px' }}>
                          {isAdmin && <button onClick={() => handleDeleteService(svc.id)} style={{ fontSize: '11px', color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer' }}>Remove</button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p style={{ fontSize: '12px', color: '#94a3b8', margin: 0 }}>No services configured.</p>
              )}

              {/* Service Form */}
              {showServiceForm && (
                <div style={{ marginTop: '12px', padding: '16px', background: '#f8f9fa', borderRadius: '8px' }}>
                  <div className="form-grid-3" style={{ gap: '10px' }}>
                    <div>
                      <label style={{ fontSize: '11px', fontWeight: 600, display: 'block', marginBottom: '2px' }}>Code *</label>
                      <input value={serviceForm.code} onChange={e => setServiceForm({...serviceForm, code: e.target.value})} placeholder="express" style={{ width: '100%', padding: '6px 10px', fontSize: '12px', border: '1px solid #e2e8f0', borderRadius: '6px', boxSizing: 'border-box' }} />
                    </div>
                    <div>
                      <label style={{ fontSize: '11px', fontWeight: 600, display: 'block', marginBottom: '2px' }}>Name *</label>
                      <input value={serviceForm.name} onChange={e => setServiceForm({...serviceForm, name: e.target.value})} placeholder="Express Delivery" style={{ width: '100%', padding: '6px 10px', fontSize: '12px', border: '1px solid #e2e8f0', borderRadius: '6px', boxSizing: 'border-box' }} />
                    </div>
                    <div>
                      <label style={{ fontSize: '11px', fontWeight: 600, display: 'block', marginBottom: '2px' }}>Service Type</label>
                      <select value={serviceForm.service_type} onChange={e => setServiceForm({...serviceForm, service_type: e.target.value})} style={{ width: '100%', padding: '6px 10px', fontSize: '12px', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
                        <option value="express">Express</option>
                        <option value="standard">Standard</option>
                        <option value="economy">Economy</option>
                        <option value="surface">Surface</option>
                        <option value="air">Air</option>
                        <option value="same_day">Same Day</option>
                        <option value="next_day">Next Day</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize: '11px', fontWeight: 600, display: 'block', marginBottom: '2px' }}>Product Code</label>
                      <input value={serviceForm.carrier_product_code} onChange={e => setServiceForm({...serviceForm, carrier_product_code: e.target.value})} placeholder="E" style={{ width: '100%', padding: '6px 10px', fontSize: '12px', border: '1px solid #e2e8f0', borderRadius: '6px', boxSizing: 'border-box' }} />
                    </div>
                    <div>
                      <label style={{ fontSize: '11px', fontWeight: 600, display: 'block', marginBottom: '2px' }}>Sub Product Code</label>
                      <input value={serviceForm.carrier_sub_product_code} onChange={e => setServiceForm({...serviceForm, carrier_sub_product_code: e.target.value})} placeholder="P" style={{ width: '100%', padding: '6px 10px', fontSize: '12px', border: '1px solid #e2e8f0', borderRadius: '6px', boxSizing: 'border-box' }} />
                    </div>
                    <div>
                      <label style={{ fontSize: '11px', fontWeight: 600, display: 'block', marginBottom: '2px' }}>Product Type</label>
                      <input type="number" value={serviceForm.carrier_product_type} onChange={e => setServiceForm({...serviceForm, carrier_product_type: Number(e.target.value)})} style={{ width: '100%', padding: '6px 10px', fontSize: '12px', border: '1px solid #e2e8f0', borderRadius: '6px', boxSizing: 'border-box' }} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '16px', marginTop: '10px', fontSize: '12px' }}>
                    <label><input type="checkbox" checked={serviceForm.supports_cod} onChange={e => setServiceForm({...serviceForm, supports_cod: e.target.checked})} /> COD</label>
                    <label><input type="checkbox" checked={serviceForm.supports_prepaid} onChange={e => setServiceForm({...serviceForm, supports_prepaid: e.target.checked})} /> Prepaid</label>
                    <label><input type="checkbox" checked={serviceForm.supports_reverse} onChange={e => setServiceForm({...serviceForm, supports_reverse: e.target.checked})} /> Reverse</label>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                    <button onClick={handleAddService} style={{ padding: '5px 12px', fontSize: '12px', fontWeight: 600, borderRadius: '6px', border: 'none', background: '#0d9488', color: 'white', cursor: 'pointer' }}>Add Service</button>
                    <button className="btn btn-cancel btn-sm" onClick={() => setShowServiceForm(false)}>Cancel</button>
                  </div>
                </div>
              )}
            </div>

            {/* Capabilities Section */}
            <div style={{ background: 'white', borderRadius: '12px', padding: '20px 24px', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
              <h4 style={{ margin: '0 0 12px', fontSize: '14px', fontWeight: 700, color: '#1e293b' }}>Capabilities</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '8px' }}>
                {CAPABILITIES_LIST.map(cap => {
                  const existing = selectedCarrier.capabilities?.find(c => c.capability === cap);
                  const isSupported = existing?.is_supported || false;
                  return (
                    <label key={cap} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px', borderRadius: '6px', background: isSupported ? '#f0fdfa' : '#f8f9fa', border: `1px solid ${isSupported ? '#99f6e4' : '#e2e8f0'}`, cursor: isAdmin ? 'pointer' : 'default', fontSize: '12px' }}>
                      <input
                        type="checkbox"
                        checked={isSupported}
                        disabled={!isAdmin}
                        onChange={e => {
                          const caps: Record<string, boolean> = {};
                          selectedCarrier.capabilities?.forEach(c => { caps[c.capability] = c.is_supported; });
                          caps[cap] = e.target.checked;
                          handleUpdateCapabilities(caps);
                        }}
                        style={{ accentColor: '#0d9488' }}
                      />
                      <span style={{ fontWeight: 500, color: '#334155', textTransform: 'capitalize' }}>{cap.replace(/_/g, ' ')}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Configuration Info */}
            <div style={{ background: 'white', borderRadius: '12px', padding: '20px 24px', border: '1px solid #e2e8f0' }}>
              <h4 style={{ margin: '0 0 12px', fontSize: '14px', fontWeight: 700, color: '#1e293b' }}>Configuration</h4>
              <div className="form-grid-2" style={{ gap: '12px', fontSize: '12px', marginBottom: 0 }}>
                <div><span style={{ color: '#64748b' }}>Auth Type:</span> <strong>{selectedCarrier.auth_type}</strong></div>
                <div><span style={{ color: '#64748b' }}>Environment:</span> <strong>{selectedCarrier.environment}</strong></div>
                <div><span style={{ color: '#64748b' }}>Priority:</span> <strong>{selectedCarrier.priority}</strong></div>
                <div><span style={{ color: '#64748b' }}>Max Weight:</span> <strong>{selectedCarrier.max_weight_kg || '—'} kg</strong></div>
                <div style={{ gridColumn: '1 / -1' }}><span style={{ color: '#64748b' }}>Sandbox URL:</span> <code style={{ fontSize: '11px', color: '#475569' }}>{selectedCarrier.base_url_sandbox || '—'}</code></div>
                <div style={{ gridColumn: '1 / -1' }}><span style={{ color: '#64748b' }}>Production URL:</span> <code style={{ fontSize: '11px', color: '#475569' }}>{selectedCarrier.base_url_production || '—'}</code></div>
                <div style={{ gridColumn: '1 / -1' }}><span style={{ color: '#64748b' }}>Tracking URL:</span> <code style={{ fontSize: '11px', color: '#475569' }}>{selectedCarrier.tracking_url_template || '—'}</code></div>
                <div><span style={{ color: '#64748b' }}>Adapter Available:</span> <strong style={{ color: selectedCarrier.adapter_available ? '#10b981' : '#ef4444' }}>{selectedCarrier.adapter_available ? 'Yes' : 'No'}</strong></div>
                <div><span style={{ color: '#64748b' }}>Countries:</span> <strong>{selectedCarrier.supported_countries?.join(', ') || 'All'}</strong></div>
              </div>
            </div>
          </div>
        )}
      </div>

      <ConfirmDeleteModal
        isOpen={deleteConfirm.show}
        title="Delete Carrier"
        message="Are you sure you want to delete this carrier? This cannot be undone."
        itemName={deleteConfirm.name}
        onConfirm={handleDeleteCarrier}
        onCancel={() => setDeleteConfirm({ show: false, id: null, name: '' })}
      />
    </div>
  );
};

export default CarrierManager;
